import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useQuery } from '@tanstack/react-query'

import type { LandlordAmendmentDefault, RentAmendment } from '../types'

export const rentAmendmentsQueryKey = ['rent-amendments']

/** Amendment history of an offer (tenant side) or a flat (landlord side). */
export function useRentAmendments(params: { offer?: string; flat?: string }) {
  async function getRentAmendments() {
    const response = await apiClient.get<RentAmendment[]>(
      `${SNA_PREFIX}rent-amendments`,
      { params }
    )
    return response.data
  }

  return useQuery({
    queryFn: getRentAmendments,
    queryKey: [...rentAmendmentsQueryKey, params],
    enabled: !!(params.offer || params.flat),
    // Signatures arrive through the DocuSeal webhook; keep the list fresh
    // while something is still waiting for them.
    refetchInterval: (query) =>
      query.state.data?.some((a) => a.status === 'SENT') ? 15_000 : false
  })
}

/**
 * A signed landlord increase on the offer's flat that the tenant has no
 * amendment for yet — the prompt to prepare one for the tenant too.
 */
export function useLandlordPendingAmendment(offerId?: string) {
  async function getLandlordPending() {
    const response = await apiClient.get<RentAmendment | null | ''>(
      `${SNA_PREFIX}rent-amendments/landlord-pending`,
      { params: { offer: offerId } }
    )
    // An empty 200 body arrives as '' — normalise to null.
    return response.data || null
  }

  return useQuery({
    queryFn: getLandlordPending,
    queryKey: [...rentAmendmentsQueryKey, 'landlord-pending', offerId],
    enabled: !!offerId
  })
}

/**
 * Default effective date of a landlord amendment: the day after the running
 * sublease ends, or next month when the flat has none.
 */
export function useLandlordAmendmentDefault(flatId?: string) {
  async function getLandlordDefault() {
    const response = await apiClient.get<LandlordAmendmentDefault>(
      `${SNA_PREFIX}rent-amendments/landlord-default`,
      { params: { flat: flatId } }
    )
    return response.data
  }

  return useQuery({
    queryFn: getLandlordDefault,
    queryKey: [...rentAmendmentsQueryKey, 'landlord-default', flatId],
    enabled: !!flatId
  })
}
